import type { MetaFunction } from "@remix-run/node";

export interface MetaConfig {
  title: string;
  description: string;
  url?: string;
  image?: string;
  noIndex?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export function createMeta(config: MetaConfig): ReturnType<MetaFunction> {
  const {
    title,
    description,
    url = "https://dewdropapp.com",
    image = "https://dewdropapp.com/dewdrop_screenshot_1.png",
    noIndex = false,
    keywords = [],
    author = "Dewdrop Beta",
    publishedTime,
    modifiedTime,
  } = config;

  const meta: ReturnType<MetaFunction> = [
    { title },
    { name: "description", content: description },
    { name: "author", content: author },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Dewdrop Beta" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { rel: "canonical", href: url },
  ];

  if (keywords.length > 0) {
    meta.push({ name: "keywords", content: keywords.join(", ") });
  }

  if (publishedTime) {
    meta.push({ property: "article:published_time", content: publishedTime });
  }

  if (modifiedTime) {
    meta.push({ property: "article:modified_time", content: modifiedTime });
  }

  if (noIndex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }

  return meta;
}

export function createDewMeta(
  dewData: {
    id: number;
    date: string;
    raindropsCount: number;
    content?: string;
  }
): ReturnType<MetaFunction> {
  const formattedDate = new Date(dewData.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const title = `Dew for ${formattedDate} - Dewdrop Beta`;
  
  const description = dewData.content
    ? `${dewData.content.slice(0, 150)}...`
    : `Dew created on ${formattedDate}. Discover curated content from ${dewData.raindropsCount} selected raindrops.`;

  const url = `https://dewdropapp.com/dews/${dewData.id}`;

  return createMeta({
    title,
    description,
    url,
    keywords: ["dew", "raindrop", "bookmarks", "articles", "digest"],
    publishedTime: dewData.date,
  });
}

export function createDashboardMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "My Dashboard - Dewdrop Beta",
    description: "Check your Dewdrop activity status and view the latest dews and raindrops. Welcome to your knowledge treasure trove.",
    url: "https://dewdropapp.com/latest",
    keywords: ["dashboard", "dew", "raindrop", "bookmarks", "activity"],
  });
}

export function createDewsIndexMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Dew List - Dewdrop Beta",
    description: "View all your created dews and reflect on daily discoveries. Rediscover new knowledge from your past insights.",
    url: "https://dewdropapp.com/dews",
    keywords: ["dew", "list", "history", "raindrop", "knowledge"],
  });
}

export function createDeleteMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Delete Account - Dewdrop Beta",
    description: "Permanently delete your Dewdrop account and all associated data. This action cannot be undone.",
    url: "https://dewdropapp.com/settings/delete-account",
    noIndex: true,
  });
}

export function createSettingsMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Settings - Dewdrop Beta",
    description: "Manage your Dewdrop account settings, notifications, and subscription preferences.",
    url: "https://dewdropapp.com/settings",
    noIndex: true,
  });
}

export function createExcludedCollectionsMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Excluded Collections - Dewdrop Beta",
    description: "Manage which Raindrop.io collections to exclude from your daily dew generation.",
    url: "https://dewdropapp.com/settings/excluded-collections",
    noIndex: true,
  });
}

export function createPricingMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Pricing - Dewdrop Beta",
    description: "Choose the right Dewdrop plan for you. Free and paid plans available with different features for rediscovering your bookmarks.",
    url: "https://dewdropapp.com/pricing",
    keywords: ["pricing", "plan", "subscription", "dewdrop"],
  });
}

export function createBillingSuccessMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Payment Successful - Dewdrop Beta",
    description: "Your Dewdrop subscription has been activated successfully.",
    url: "https://dewdropapp.com/billing/success",
    noIndex: true,
  });
}

export function createBillingCancelMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Payment Cancelled - Dewdrop Beta",
    description: "Your payment was cancelled. You can try again anytime.",
    url: "https://dewdropapp.com/billing/cancel",
    noIndex: true,
  });
}

export function createTopMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Dewdrop Beta - Rediscover Your Forgotten Raindrops",
    description:
      "Transform your forgotten bookmarks into daily discoveries. Get randomized summaries of your Raindrop.io collection delivered to your inbox.",
    url: "https://dewdropapp.com",
    keywords: ["dewdrop", "raindrop", "bookmarks", "daily summary", "rediscover"],
  });
}

export function createSigninMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Sign In - Dewdrop",
    description: "Sign in to Dewdrop with your Raindrop.io account",
    url: "https://dewdropapp.com/signin",
    noIndex: true,
  });
}

export function createTermsMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Terms of Service - Dewdrop",
    description:
      "Terms of Service for Dewdrop - Rediscover Your Forgotten Raindrops",
    url: "https://dewdropapp.com/terms",
    noIndex: true,
  });
}

export function createPrivacyMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Privacy Policy - Dewdrop",
    description:
      "Privacy Policy for Dewdrop - How we collect, use, and protect your personal information",
    url: "https://dewdropapp.com/privacy",
    noIndex: true,
  });
}

export function createDemoMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "Demo - Dewdrop",
    description:
      "Experience Dewdrop's daily curated bookmark summaries. Try our service with this interactive demo featuring sample content from popular websites.",
    url: "https://dewdropapp.com/demo",
    keywords: ["dewdrop", "demo", "bookmarks", "raindrop", "daily summary", "productivity", "curation"],
    noIndex: true,
  });
}

export function createDemoDetailMeta(dewDate: string): ReturnType<MetaFunction> {
  return createMeta({
    title: `Demo Dew ${dewDate} - Dewdrop`,
    description: `Experience Dewdrop's daily curated bookmark summaries. Demo featuring sample content from popular websites for ${dewDate}.`,
    url: "https://dewdropapp.com/demo",
    keywords: ["dewdrop", "demo", "bookmarks", "raindrop", "daily summary", "productivity", "curation"],
    noIndex: true,
  });
}

export function createNotFoundMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "404 - Page Not Found | Dewdrop",
    description: "The page you're looking for could not be found.",
    url: "https://dewdropapp.com",
    noIndex: true,
  });
}

export function createSpecifiedCommercialTransactionsMeta(): ReturnType<MetaFunction> {
  return createMeta({
    title: "特定商取引法に基づく表記 - Dewdrop",
    description:
      "Dewdropの特定商取引法に基づく表記ページです。販売業者、価格、支払い方法、返金ポリシーについての情報を掲載しています。",
    url: "https://dewdropapp.com/specified-commercial-transactions",
    noIndex: true,
  });
}